import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Mail, CheckCircle2, XCircle, RefreshCw, BarChart3, Server, Shield,
} from "lucide-react";
import { API } from "../../config";

const TYPE_LABEL = {
  welcome: "Welcome",
  otp: "OTP Firma",
  contract_signed: "Contratto firmato",
  contract_admin_notify: "Notifica admin",
  sms_credit_alert: "Alert sistema",
  dealer_welcome: "Welcome Dealer",
  other: "Altro",
};

export const EmailHealthWidget = ({ variant = "admin" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/dashboard/email-health?days=14`);
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 60_000);
    return () => clearInterval(t);
  }, []);

  if (!data) {
    return (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
        {loading ? <div className="spinner mx-auto" /> : <p className="text-gray-500 text-sm">Nessun dato</p>}
      </div>
    );
  }

  const totals = data.totals || {};
  const status24 = data.status_24h || {};
  const status30 = data.status_30d || {};
  const timeseries = data.timeseries || [];
  const maxTs = Math.max(1, ...timeseries.map(d => (d.sent || 0) + (d.failed || 0)));
  const esp = data.esp_api_stats || {};

  return (
    <div className="bg-gradient-to-br from-[#0f1a17] via-[#0a0f0d] to-[#0f1a17] border border-lojack-blue-500/20 rounded-2xl p-6 space-y-5" data-testid="email-health-widget">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-lojack-blue-500/10 flex items-center justify-center">
            <Mail size={20} className="text-lojack-blue-400" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Email Health</h3>
            <p className="text-gray-500 text-xs">Provider Email · monitoraggio in tempo reale</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs flex items-center gap-1.5 ${data.transport_configured ? "text-lojack-blue-400" : "text-red-400"}`}>
            <span className={`w-2 h-2 rounded-full ${data.transport_configured ? "bg-lojack-blue-500 animate-pulse" : "bg-red-500"}`} />
            {data.transport_configured ? "Online" : "Offline"}
          </span>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-lojack-blue-400 transition-colors"
            data-testid="email-health-refresh"
            title="Aggiorna"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Volumes row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="email-volume-24h">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Inviate 24h</p>
          <p className="text-3xl font-bold text-white">{totals.last_24h ?? 0}</p>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="text-lojack-blue-400 flex items-center gap-1"><CheckCircle2 size={11} />{status24.sent ?? 0}</span>
            <span className="text-red-400 flex items-center gap-1"><XCircle size={11} />{status24.failed ?? 0}</span>
          </div>
          <p className="text-gray-500 text-[10px] mt-1.5">Delivery: {data.delivery_rate_24h}%</p>
        </div>

        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="email-volume-7d">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Inviate 7g</p>
          <p className="text-3xl font-bold text-white">{totals.last_7d ?? 0}</p>
          <p className="text-gray-500 text-[10px] mt-3">Settimana mobile</p>
        </div>

        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="email-volume-30d">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Inviate 30g</p>
          <p className="text-3xl font-bold text-white">{totals.last_30d ?? 0}</p>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="text-lojack-blue-400 flex items-center gap-1"><CheckCircle2 size={11} />{status30.sent ?? 0}</span>
            <span className="text-red-400 flex items-center gap-1"><XCircle size={11} />{status30.failed ?? 0}</span>
          </div>
          <p className="text-gray-500 text-[10px] mt-1.5">Delivery: {data.delivery_rate_30d}%</p>
        </div>

        {/* ESP API panel */}
        <div className={`border rounded-xl p-4 ${esp.available ? "bg-black/40 border-gray-800" : "bg-gray-900/30 border-gray-800/50"}`} data-testid="email-esp-stats">
          <div className="flex items-center gap-2 mb-2">
            <Server size={12} className={esp.available ? "text-lojack-blue-400" : "text-gray-600"} />
            <p className="text-gray-400 text-xs uppercase tracking-wider">Provider Stats</p>
          </div>
          {esp.available ? (
            <>
              <p className="text-xs text-gray-500">Quota 24h</p>
              <p className="text-lg font-bold text-white">{esp.quota_sent_last_24h} / {esp.quota_max_24h}</p>
              <p className={`text-[10px] mt-1 ${esp.bounce_rate_pct > 5 ? "text-red-400" : esp.bounce_rate_pct > 2 ? "text-lojack-clause-400" : "text-lojack-blue-400"}`}>
                Bounce: {esp.bounce_rate_pct}% · Complaint: {esp.complaint_rate_pct}%
              </p>
            </>
          ) : (
            <p className="text-gray-600 text-xs leading-relaxed">
              {esp.reason || "Non disponibile"}
            </p>
          )}
        </div>
      </div>

      {/* Timeseries */}
      <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="email-timeseries">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 size={14} className="text-lojack-blue-400" />
          <p className="text-white text-sm font-semibold">Andamento ultimi 14 giorni</p>
        </div>
        <div className="flex items-end gap-1 h-24">
          {timeseries.map((d, i) => {
            const total = (d.sent || 0) + (d.failed || 0);
            const heightPct = (total / maxTs) * 100;
            const sentPct = total > 0 ? (d.sent / total) * 100 : 0;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                <div className="w-full bg-gray-800/50 rounded-t overflow-hidden flex flex-col justify-end" style={{ height: `${heightPct || 2}%`, minHeight: "2px" }}>
                  {total > 0 && (
                    <>
                      <div className="bg-red-500/70" style={{ height: `${100 - sentPct}%` }} />
                      <div className="bg-lojack-blue-500/70" style={{ height: `${sentPct}%` }} />
                    </>
                  )}
                </div>
                <p className="text-gray-600 text-[9px]">{d.date}</p>
                {total > 0 && (
                  <div className="absolute bottom-full mb-1 hidden group-hover:block bg-black border border-gray-700 rounded px-2 py-1 text-[10px] whitespace-nowrap z-10">
                    <span className="text-lojack-blue-400">{d.sent} sent</span> · <span className="text-red-400">{d.failed} failed</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* By type + top failures */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="email-by-type">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-3">Per tipo (14g)</p>
          {data.by_type.length === 0 ? (
            <p className="text-gray-600 text-xs">Nessun invio nel periodo</p>
          ) : (
            <div className="space-y-2">
              {data.by_type.map((row) => (
                <div key={row.type} className="flex items-center justify-between text-sm">
                  <span className="text-gray-300">{TYPE_LABEL[row.type] || row.type}</span>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-lojack-blue-400">{row.sent} ok</span>
                    {row.failed > 0 && <span className="text-red-400">{row.failed} ko</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {variant === "admin" && data.top_failures && data.top_failures.length > 0 && (
          <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="email-top-failures">
            <p className="text-gray-400 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
              <XCircle size={12} className="text-red-400" />Ultimi falliti (7g)
            </p>
            <div className="space-y-1.5">
              {data.top_failures.slice(0, 5).map((f, i) => (
                <div key={i} className="flex items-center justify-between text-xs gap-3">
                  <span className="text-gray-400 font-mono truncate max-w-[180px]">{f.recipient_masked}</span>
                  <span className="text-gray-500 truncate flex-1">{(f.error || "").slice(0, 50)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {variant === "it" && (
          <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="email-security-info">
            <p className="text-gray-400 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
              <Shield size={12} className="text-lojack-blue-400" />Sicurezza Transport
            </p>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-gray-500">Crittografia</span><span className="text-lojack-blue-400">TLS 1.2+</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Autenticazione</span><span className="text-lojack-blue-400">SMTP-AUTH</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Mittente</span><span className="text-gray-300">Verificato</span></div>
            </div>
          </div>
        )}
      </div>

      <p className="text-gray-600 text-[10px] text-center">
        Aggiornato: {new Date(data.generated_at).toLocaleString("it-IT")} · Auto-refresh 60s
      </p>
    </div>
  );
};

export default EmailHealthWidget;

EmailHealthWidget.propTypes = {
  variant: PropTypes.oneOf(["admin", "it"]),    // defaults to "admin" in destructure
};
